/* eslint-disable complexity */

import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { Logger, ROUTES, SfaSetupNotCompleteFatalError, useCookieBannerGuard } from '@futrli/shared-services';

import DialogFullScreen from 'carbon-react/lib/components/dialog-full-screen';

import { SFA_FATAL_ERROR, SFA_LINK_OPTIONS } from '../../sfa-types';
import { SfaCancelByUserFatalError } from '../../components/sfa/fatal-errors/sfa-cancel-by-user-fatal-error';
import { SfaChooseConnectionTypeContainer } from './sfa-choose-connection-type-container';
import { SfaFinishedContainer } from './sfa-finished-container';
import { SfaLinkBusinessToOrgContainer } from './sfa-link-business-to-org-container';
import { SfaLoading } from '../../components/sfa/sfa-loading';
import { SfaNoWorkspacesFatalError } from '../../components/sfa/fatal-errors/sfa-no-workspaces-fatal-error';
import { SfaSomethingWentWrongFatalError } from '../../components/sfa/fatal-errors/sfa-something-went-wrong-fatal-error';
import { SfaUserCannotAccessAllOrgsFatalError } from '../../components/sfa/fatal-errors/sfa-user-cannot-access-all-orgs-fatal-error';
import { SfaUserCannotCreateNewOrgFatalError } from '../../components/sfa/fatal-errors/sfa-user-cannot-create-new-org-fatal-error';
import { useSfaOnboardingContext } from '../../contexts/sfa-onboarding-context';

enum STEPS {
  ADDITIONAL_INFO = 'ADDITIONAL_INFO',
  CHOOSE_ORG_TO_LINK = 'CHOOSE_ORG_TO_LINK',
  CHOOSE_CONNECTION_TYPE = 'CHOOSE_CONNECTION_TYPE',
  FINISHED = 'FINISHED'
}

export const SfaOnboardingContainer = () => {
  const {
    error,
    finishedOrganisation,
    isLoading,
    linkingData,
    organisation,
    orgIdToLink,
    setError,
    setOrgIdToLink,
    workspace
  } = useSfaOnboardingContext();

  const [currentStep, setCurrentStep] = useState<STEPS>();

  const intl = useIntl();
  const text = {
    title: intl.formatMessage({ id: 'onboarding.sfa.welcome_to_futrli', defaultMessage: 'Welcome to Futrli' })
  };

  useCookieBannerGuard();

  const updateCurrentStep = useCallback(() => {
    if (organisation?.is_holding === false || finishedOrganisation) {
      // Org is already setup (not in pending setup), skip sfa onboarding
      setCurrentStep(STEPS.FINISHED);
      return;
    }

    // If we have organisation data, we have enough data to start SFA flow logic.
    // If there isn't organisation data, an error would have been set in the context
    if (organisation && linkingData) {
      if (!linkingData.user_can_set_up_holding_org) {
        // User does not permission to set up holding org, show error screen
        setError(SFA_FATAL_ERROR.USER_CANNOT_CREATE_NEW_ORG);
        return;
      }

      if (linkingData.has_orgs_available_for_linking && !linkingData.user_can_link_all_available_orgs) {
        // The user does not have admin access to all orgs in the workspace, show error screen
        setError(SFA_FATAL_ERROR.USER_CANNOT_ACCESS_ALL_ORGS);
        return;
      }

      if (!linkingData.has_orgs_available_for_linking) {
        // There are no chargebee orgs to link, so jump straight to asking for connection type
        setOrgIdToLink(SFA_LINK_OPTIONS.NEW);
        setCurrentStep(STEPS.CHOOSE_CONNECTION_TYPE);
        return;
      }

      if (!orgIdToLink) {
        // User needs to choose either to link an existing org, or to set holding org up as a new org
        setCurrentStep(STEPS.CHOOSE_ORG_TO_LINK);
        return;
      }

      if (orgIdToLink === SFA_LINK_OPTIONS.NEW) {
        // User chose to set holding org up as a new org, user needs to choose connection type
        setCurrentStep(STEPS.CHOOSE_CONNECTION_TYPE);
        return;
      }
    }
  }, [finishedOrganisation, linkingData, orgIdToLink, organisation, setError, setOrgIdToLink]);

  const getContent = () => {
    switch (currentStep) {
      case STEPS.CHOOSE_ORG_TO_LINK: {
        return <SfaLinkBusinessToOrgContainer />;
      }
      case STEPS.CHOOSE_CONNECTION_TYPE: {
        return <SfaChooseConnectionTypeContainer />;
      }
      case STEPS.FINISHED: {
        return (
          <SfaFinishedContainer
            workspaceId={workspace?.id || ''}
            organisationId={finishedOrganisation?.id || organisation?.id || ''}
            datasourceId={finishedOrganisation?.datasource?.id || organisation?.datasource?.id || ''}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  const handleBackToSetup = () => {
    // Clear selected org id and error in context to reset onboarding process
    setOrgIdToLink(undefined);
    setError(undefined);
  };

  useEffect(() => {
    updateCurrentStep();
  }, [updateCurrentStep]);

  if (error) {
    switch (error) {
      case SFA_FATAL_ERROR.BUSINESS_NOT_FOUND: {
        return <SfaSetupNotCompleteFatalError />;
      }
      case SFA_FATAL_ERROR.CANCEL_BY_USER: {
        return <SfaCancelByUserFatalError onBackToSetup={handleBackToSetup} />;
      }
      case SFA_FATAL_ERROR.ERROR_CREATING_LINK: {
        return <SfaSomethingWentWrongFatalError />;
      }
      case SFA_FATAL_ERROR.NO_WORKSPACES: {
        return <SfaNoWorkspacesFatalError />;
      }
      case SFA_FATAL_ERROR.USER_CANNOT_ACCESS_ALL_ORGS: {
        return <SfaUserCannotAccessAllOrgsFatalError />;
      }
      case SFA_FATAL_ERROR.USER_CANNOT_CREATE_NEW_ORG: {
        return <SfaUserCannotCreateNewOrgFatalError />;
      }
      default: {
        Logger.error(`SFA onboarding error: ${error}`);
        return <Navigate to={ROUTES.ROOT_ROUTE} />;
      }
    }
  }

  if (isLoading) {
    return <SfaLoading />;
  }

  return (
    <DialogFullScreen open disableClose disableEscKey disableContentPadding title={text.title}>
      {getContent()}
    </DialogFullScreen>
  );
};
